import React, { useMemo } from 'react';
import moment from 'moment';
import styles from './ShipmentSummaryDrawer.module.css';
import { getAdditionalReferences, getReferenceValue } from '../../utils/shipmentReferences';

const DOCUMENTS = [
  { key: 'bol', label: 'BOL', fields: ['bol_document'] },
  { key: 'ratecon', label: 'RATECON', fields: ['rate_confirmation_document'] },
  { key: 'pod', label: 'POD', fields: ['pod_document'] },
  { key: 'dispatch', label: 'DISPATCH', fields: ['dispatch_sheet'] },
];

const deliveredStatuses = new Set(['Delivered', 'Delivered — Ready to Bill', 'Complete']);

const formatStopType = (value = '') => {
  const normalized = value.toLowerCase();
  if (normalized.includes('pick')) return 'PICKUP';
  if (normalized.includes('del')) return 'DELIVERY';
  return value.toUpperCase() || 'STOP';
};

const formatWindow = (stop) => {
  const start = stop?.window_start ? moment(stop.window_start) : null;
  const end = stop?.window_end ? moment(stop.window_end) : null;
  if (!start?.isValid()) return 'Schedule pending';

  const date = start.format('MMM D');
  const startTime = start.format('HH:mm');
  const endTime = end?.isValid() ? end.format('HH:mm') : null;
  return `${date} · ${endTime ? `${startTime}–${endTime}` : startTime}`;
};

const getLocation = (stop) => {
  const location = stop?.location || {};
  return [location.city, location.state].filter(Boolean).join(', ') || location.company_name || location.name || 'Location pending';
};

const resolveAppointmentType = (stop) => {
  if (stop?.appointment_type === 'Appointment') return 'APPT';
  if (stop?.appointment_type === 'FCFS') return 'FCFS';
  return stop?.appointment_required ? 'APPT' : 'FCFS';
};

const normalizeStops = (load) => {
  if (Array.isArray(load?.stops) && load.stops.length) {
    return [...load.stops].sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0));
  }

  return [
    load?.shipper && {
      sequence: 1,
      stop_type: 'pickup',
      window_start: load.pickup_date,
      appointment_type: load.pickup_appointment_type,
      location: load.shipper,
    },
    load?.consignee && {
      sequence: 2,
      stop_type: 'delivery',
      window_start: load.delivery_date,
      appointment_type: load.delivery_appointment_type,
      location: load.consignee,
    },
  ].filter(Boolean);
};

const getDocumentState = (document, load) => {
  const isAvailable = document.fields.some((field) => Boolean(load?.[field]));
  if (isAvailable) return 'Available';
  if (document.key === 'pod') return deliveredStatuses.has(load?.status) ? 'Unavailable' : 'Pending';
  return 'Not generated';
};

const StopReadout = ({ stop }) => {
  if (!stop) {
    return <span className={styles.emptyValue}>Stop unavailable</span>;
  }

  const appointment = resolveAppointmentType(stop);
  const sequence = String(stop.sequence || '—').padStart(2, '0');

  return (
    <div className={styles.stopReadout}>
      <div className={styles.stopHeading}>
        <span className={styles.sequence}>{sequence}</span>
        <strong>{formatStopType(stop.stop_type || stop.type)}</strong>
        <span className={styles.stopLocation}>{getLocation(stop)}</span>
      </div>
      <div className={styles.stopMeta}>
        <span>{formatWindow(stop)}</span>
        <span className={styles.scheduleType}>{appointment}</span>
        {appointment === 'APPT' && stop.appointment_number && <span>#{stop.appointment_number}</span>}
        <span className={styles.pending}>ETA pending</span>
      </div>
    </div>
  );
};

const ShipmentSummaryDrawer = ({ load, isLoading, error, onOpenWorkspace }) => {
  const stops = useMemo(() => normalizeStops(load), [load]);
  const firstStop = stops[0];
  const finalStop = stops.length > 1 ? stops[stops.length - 1] : null;
  const intermediateCount = Math.max(stops.length - 2, 0);
  const bolNumber = getReferenceValue(load, 'bol');
  const poNumber = getReferenceValue(load, 'po');
  const additionalReferences = getAdditionalReferences(load);

  if (isLoading && !load) {
    return <div className={styles.feedback}>Syncing shipment details…</div>;
  }

  return (
    <section className={styles.drawer} aria-label="Shipment summary">
      {isLoading && <div className={styles.syncing}>Refreshing shipment detail…</div>}
      {error && <div className={styles.error}>Live detail refresh failed. Showing available shipment data.</div>}

      <div className={styles.stateBand}>
        <div className={styles.readout}>
          <span className={styles.label}>Shipment status</span>
          <strong className={styles.status}>{load?.status || 'Status unavailable'}</strong>
        </div>
        <div className={styles.readout}>
          <span className={styles.label}>Tracking</span>
          <strong>Tracking not connected</strong>
        </div>
        <div className={styles.readout}>
          <span className={styles.label}>Current location</span>
          <strong>Location unavailable</strong>
        </div>
        <div className={styles.readout}>
          <span className={styles.label}>Last ping</span>
          <strong>Last ping unavailable</strong>
        </div>
        <div className={styles.readout}>
          <span className={styles.label}>On-time risk</span>
          <strong>Unavailable</strong>
        </div>
      </div>

      <div className={styles.routeBand}>
        <div className={styles.stopCell}>
          <span className={styles.label}>{stops.length > 1 ? 'First route stop' : 'Route stop'}</span>
          <StopReadout stop={firstStop} />
        </div>
        <div className={styles.stopCell}>
          <span className={styles.label}>Final route stop</span>
          <StopReadout stop={finalStop} />
        </div>
        <div className={styles.routeCount}>
          <span className={styles.label}>Route</span>
          <strong>{intermediateCount ? `+${intermediateCount} intermediate` : `${stops.length} stop${stops.length === 1 ? '' : 's'}`}</strong>
          <button type="button" onClick={onOpenWorkspace}>View all stops</button>
        </div>
      </div>

      <div className={styles.utilityBand}>
        <div className={styles.identity}>
          <span><small>BOL</small>{bolNumber || 'Not provided'}</span>
          <span><small>PO</small>{poNumber || 'Not provided'}</span>
          {additionalReferences.map((reference, index) => (
            <span key={`${reference.type}-${reference.label || ''}-${index}`}>
              <small>{reference.label || reference.type}</small>{reference.value}
            </span>
          ))}
          <span><small>EQUIPMENT</small>{load?.equipment_type || 'Not provided'}</span>
        </div>
        <div className={styles.documents} aria-label="Document manifest">
          {DOCUMENTS.map((document) => {
            const state = getDocumentState(document, load);
            return (
              <span key={document.key} className={state === 'Available' ? styles.documentAvailable : ''}>
                <small>{document.label}</small>{state}
              </span>
            );
          })}
        </div>
        <button className={styles.primaryAction} type="button" onClick={onOpenWorkspace}>
          Open workspace
        </button>
      </div>
    </section>
  );
};

export { normalizeStops, getDocumentState };
export default ShipmentSummaryDrawer;
